import React, { createContext, useContext, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import useUserLanguage, { 
  SupportedLanguage, 
  SUPPORTED_LANGUAGES,
  LanguageInfo,
  isRtlLanguage
} from "@/hooks/useUserLanguage";
import LanguageSelectorModal from "@/components/LanguageSelectorModal";

// Routes where multilingual features (modal + language switching) are active
const MULTILINGUAL_ROUTE_PREFIXES = [
  "/cultural-onboarding",
  "/course",
  "/login",
];

function isMultilingualRoute(pathname: string): boolean {
  return MULTILINGUAL_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

interface LanguageContextValue {
  language: SupportedLanguage;
  isLoading: boolean;
  setLanguage: (lang: SupportedLanguage) => Promise<void>;
  getTranslatedContent: (contentKey: string, fallbackText: string) => Promise<string>;
  languages: LanguageInfo[];
  currentLanguageInfo: LanguageInfo;
  isRtl: boolean;
  isMultilingual: boolean;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const isMultilingual = isMultilingualRoute(location.pathname);

  const {
    language,
    isLoading,
    needsOnboarding,
    setLanguage,
    getTranslatedContent,
  } = useUserLanguage();

  // On public (non-multilingual) routes, always use German
  const effectiveLanguage: SupportedLanguage = isMultilingual ? language : "de";

  const currentLanguageInfo = SUPPORTED_LANGUAGES.find(l => l.code === effectiveLanguage) 
    || SUPPORTED_LANGUAGES[0];

  const value: LanguageContextValue = {
    language: effectiveLanguage,
    isLoading,
    setLanguage,
    getTranslatedContent,
    languages: SUPPORTED_LANGUAGES,
    currentLanguageInfo,
    isRtl: isRtlLanguage(effectiveLanguage),
    isMultilingual,
  };

  // Only show modal on multilingual routes
  const showModal = isMultilingual && needsOnboarding && !isLoading;

  return (
    <LanguageContext.Provider value={value}>
      {children}
      <LanguageSelectorModal
        isOpen={showModal}
        onSelectLanguage={setLanguage}
      />
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
